import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Mirrors link-backend/src/delegations. */
export interface DelegationPerson {
  userId: string;
  userName: string;
  name: string;
  department: string;
  profileImg: string;
}
export interface PowerInfo {
  key: string;
  module: 'Tasks' | 'Finance' | 'Lead tracker' | string;
  label: string;
  description: string;
  onBehalf: boolean;
}
export interface Delegation {
  id: string;
  delegate: DelegationPerson;
  actsFor: DelegationPerson;
  powers: string[];
  note: string;
  endsOn: string | null;
  active: boolean;
  expired: boolean;
  grantedBy: DelegationPerson;
  revokedAt: string | null;
  revokedBy: DelegationPerson | null;
  history: { at: string; by: DelegationPerson; action: string; powers?: string[]; reason?: string }[];
  createdAt: string;
}
export interface MyPowers {
  admin: boolean;
  powers: string[];
  actsFor: Record<string, DelegationPerson[]>;
  catalogue: PowerInfo[];
}

export const POWER = {
  TASKS_ASSIGN_ON_BEHALF: 'tasks.assign_on_behalf',
  TASKS_VIEW_ALL: 'tasks.view_all',
  FINANCE_APPROVE_SCOPE_CHANGES: 'finance.approve_scope_changes',
  FINANCE_APPROVE_CREDIT_NOTES: 'finance.approve_credit_notes',
  FINANCE_APPROVE_VENDOR_REQUESTS: 'finance.approve_vendor_requests',
  LEADS_ASSIGN: 'leads.assign',
} as const;

/**
 * Delegated powers ("sub-admin"): what the logged-in user may do beyond their
 * department, and the Admin screen to grant and revoke.
 */
@Injectable({ providedIn: 'root' })
export class DelegationsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.host}delegations`;
  /** The caller's own powers; loaded once per session, refreshed after grants. */
  readonly me = signal<MyPowers | null>(null);
  private mePromise: Promise<MyPowers> | null = null;

  private unwrap<T>(res: any): T {
    return (res?.data ?? res) as T;
  }

  /** Cached; pass force after a grant or revoke. */
  loadMe(force = false): Promise<MyPowers> {
    if (!force && this.mePromise) return this.mePromise;
    this.mePromise = firstValueFrom(this.http.get(`${this.base}/me`))
      .then((r) => {
        const m = this.unwrap<MyPowers>(r);
        this.me.set(m);
        return m;
      })
      .catch((e) => {
        this.mePromise = null;
        throw e;
      });
    return this.mePromise;
  }

  has(power: string): boolean {
    const m = this.me();
    return !!m && (m.admin || m.powers.includes(power));
  }

  /** Admins this user may act for under a power. */
  actsFor(power: string): DelegationPerson[] {
    return this.me()?.actsFor?.[power] || [];
  }

  async catalogue(): Promise<PowerInfo[]> {
    return this.unwrap<PowerInfo[]>(await firstValueFrom(this.http.get(`${this.base}/catalogue`)));
  }
  async list(): Promise<Delegation[]> {
    return this.unwrap<Delegation[]>(await firstValueFrom(this.http.get(this.base)));
  }
  async grant(body: { userId: string; powers: string[]; actsForUserId?: string; endsOn?: string | null; note?: string }): Promise<Delegation> {
    const d = this.unwrap<Delegation>(await firstValueFrom(this.http.post(this.base, body)));
    void this.loadMe(true);
    return d;
  }
  async revoke(id: string, reason = ''): Promise<Delegation> {
    const d = this.unwrap<Delegation>(await firstValueFrom(this.http.delete(`${this.base}/${id}`, { body: { reason } })));
    void this.loadMe(true);
    return d;
  }
}
